import { createRoot, type Root } from 'react-dom/client';
import type { SuggestionOptions, SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';
import { SlashCommandMenu, type SlashCommandMenuHandle } from './slash-command-menu';
import type { SlashCommand } from './types';

export type SuggestionRenderState = {
  /** Whether the suggestion menu is showing items (not just triggered) */
  isOpen: boolean;
};

/**
 * Creates a tiptap suggestion `render()` function that bridges the vanilla DOM
 * lifecycle hooks into our React `<SlashCommandMenu>` component.
 *
 * The menu is rendered into a portal div appended to the editor's parent so it
 * inherits the app's CSS context (theme tokens, vanilla extract styles).
 *
 * @param state - Mutable state object shared with the Input component so it
 *   can check whether the suggestion menu is active (e.g. to prevent
 *   Enter-to-submit while the menu is open).
 */
export function createSuggestionRender(state: SuggestionRenderState): SuggestionOptions<SlashCommand>['render'] {
  return () => {
    let root: Root | null = null;
    let container: HTMLDivElement | null = null;
    let menuRef: SlashCommandMenuHandle | null = null;

    return {
      onStart(props: SuggestionProps<SlashCommand>) {
        state.isOpen = props.items.length > 0;
        container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.zIndex = '50';

        // Mount to the editor wrapper so the menu positions relative to it.
        // Walk up: ProseMirror div → EditorContent → tiptapEditorWrapper.
        // Must append before positioning so offsetParent is available.
        const editorWrapper = props.editor.view.dom.parentElement?.parentElement;
        const mountTarget = editorWrapper ?? document.body;
        mountTarget.appendChild(container);

        // Position above the cursor (after mount so offsetParent is set)
        positionContainer(container, props.clientRect ?? null);

        root = createRoot(container);
        root.render(
          <SlashCommandMenu
            ref={(handle) => {
              menuRef = handle;
            }}
            items={props.items}
            command={props.command}
          />,
        );
      },

      onUpdate(props: SuggestionProps<SlashCommand>) {
        state.isOpen = props.items.length > 0;
        positionContainer(container, props.clientRect ?? null);

        root?.render(
          <SlashCommandMenu
            ref={(handle) => {
              menuRef = handle;
            }}
            items={props.items}
            command={props.command}
          />,
        );
      },

      onKeyDown(props: SuggestionKeyDownProps) {
        if (props.event.key === 'Escape') {
          // Return false so the suggestion plugin handles Escape itself
          // (calls onExit to close the menu and reset state.isOpen).
          return false;
        }
        return menuRef?.onKeyDown(props.event) ?? false;
      },

      onExit() {
        state.isOpen = false;
        // Defer unmount to avoid React warning about synchronous unmount
        // during a render cycle. queueMicrotask runs after the current task
        // but before the next paint — tighter than setTimeout(0).
        const rootToUnmount = root;
        const containerToRemove = container;
        root = null;
        container = null;
        menuRef = null;

        queueMicrotask(() => {
          rootToUnmount?.unmount();
          containerToRemove?.remove();
        });
      },
    };
  };
}

function positionContainer(container: HTMLDivElement | null, clientRect: (() => DOMRect | null) | null) {
  if (container == null || clientRect == null) return;
  const rect = clientRect();
  if (rect == null) return;

  // Position the menu above the slash command text
  const parentRect = container.offsetParent?.getBoundingClientRect() ?? { left: 0, top: 0 };
  container.style.left = `${rect.left - parentRect.left}px`;
  container.style.bottom = `${
    (container.offsetParent?.clientHeight ?? window.innerHeight) - (rect.top - parentRect.top) + 4
  }px`;
  container.style.top = 'auto';
}
